/* eslint-disable */
import React, { useEffect, useState, useRef, useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faChevronLeft,
  faChevronRight,
  faDownload,
  faSearch,
  faHome,
  faSync,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { Link } from "react-router-dom";
import moment from "moment";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import "../../components/skeletonRow.css";

const WhatsappOptinReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [distinctInterests, setDistinctInterests] = useState([]);

  // Form input state (local to input fields before user presses Enter / applies)
  const [formInputs, setFormInputs] = useState({
    subscriber_code: "",
    fullname: "",
    mobileno: "",
    email: "",
    interest: "",
    interest_name: "",
    lead_status: "",
    startDate: null,
    endDate: null,
  });

  // Active filters applied to the API call
  const [filters, setFilters] = useState({
    subscriber_code: "",
    fullname: "",
    mobileno: "",
    email: "",
    interest: "",
    interest_name: "",
    lead_status: "",
    startDate: "",
    endDate: "",
    sortBy: "id",
    sortOrder: "DESC",
    page: 1,
    limit: 100,
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    showingRecords: "Showing 0 to 0 of 0 entries",
  });

  // Fetch report data
  const fetchData = useCallback(
    async (pageNumber = 1) => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");

        const params = {
          page: pageNumber,
          limit: filters.limit,
          subscriber_code: filters.subscriber_code || undefined,
          fullname: filters.fullname || undefined,
          mobileno: filters.mobileno || undefined,
          email: filters.email || undefined,
          interest: filters.interest || undefined,
          interest_name: filters.interest_name || undefined,
          lead_status: filters.lead_status || undefined,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          sortBy: filters.sortBy || "id",
          sortOrder: filters.sortOrder || "DESC",
        };

        const response = await axios.get(`${API_URL}report-whatsapp-optin`, {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "*/*",
          },
        });

        if (response.data && response.data.success) {
          const res = response.data;
          setData(res.data || []);
          const totalRecords = res.total || 0;
          const totalPages = Math.ceil(totalRecords / filters.limit) || 1;
          const startRecord = totalRecords === 0 ? 0 : (pageNumber - 1) * filters.limit + 1;
          const endRecord = Math.min(pageNumber * filters.limit, totalRecords);

          setPagination({
            currentPage: pageNumber,
            totalPages,
            totalRecords,
            showingRecords: `Showing ${startRecord} to ${endRecord} of ${totalRecords} entries`,
          });

          if (res.distinctInterests) {
            setDistinctInterests(res.distinctInterests);
          }
        }
      } catch (error) {
        console.error("Error fetching WhatsApp opt-in data:", error);
        setData([]);
        showToast.error("Failed to load WhatsApp opt-in data");
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  useEffect(() => {
    fetchData(filters.page);
  }, [filters.page, filters.sortBy, filters.sortOrder, filters.interest, filters.startDate, filters.endDate]);

  // Form input change handlers
  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    setFormInputs((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      applyFilters();
    }
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setFormInputs((prev) => ({
      ...prev,
      startDate: start,
      endDate: end,
    }));

    if (start && end) {
      setFilters((prev) => ({
        ...prev,
        startDate: moment(start).format("YYYY-MM-DD"),
        endDate: moment(end).format("YYYY-MM-DD"),
        page: 1,
      }));
    } else if (!start && !end) {
      setFilters((prev) => ({
        ...prev,
        startDate: "",
        endDate: "",
        page: 1,
      }));
    }
  };

  const handleInterestSelect = (e) => {
    const value = e.target.value;
    setFormInputs((prev) => ({ ...prev, interest: value }));
    setFilters((prev) => ({ ...prev, interest: value, page: 1 }));
  };

  const applyFilters = () => {
    setFilters((prev) => ({
      ...prev,
      subscriber_code: formInputs.subscriber_code,
      fullname: formInputs.fullname,
      mobileno: formInputs.mobileno,
      email: formInputs.email,
      interest: formInputs.interest,
      interest_name: formInputs.interest_name,
      lead_status: formInputs.lead_status,
      startDate: formInputs.startDate ? moment(formInputs.startDate).format("YYYY-MM-DD") : "",
      endDate: formInputs.endDate ? moment(formInputs.endDate).format("YYYY-MM-DD") : "",
      page: 1,
    }));
  };

  const clearFilters = () => {
    setFormInputs({
      subscriber_code: "",
      fullname: "",
      mobileno: "",
      email: "",
      interest: "",
      interest_name: "",
      lead_status: "",
      startDate: null,
      endDate: null,
    });
    setFilters({
      subscriber_code: "",
      fullname: "",
      mobileno: "",
      email: "",
      interest: "",
      interest_name: "",
      lead_status: "",
      startDate: "",
      endDate: "",
      sortBy: "id",
      sortOrder: "DESC",
      page: 1,
      limit: 100,
    });
  };

  const hasAnyFilter = () => {
    return Boolean(
      filters.subscriber_code ||
        filters.fullname ||
        filters.mobileno ||
        filters.email ||
        filters.interest ||
        filters.interest_name ||
        filters.lead_status ||
        filters.startDate ||
        filters.endDate
    );
  };

  // Sorting
  const handleSort = (column) => {
    const isSameCol = filters.sortBy === column;
    const newOrder = isSameCol && filters.sortOrder === "ASC" ? "DESC" : "ASC";
    setFilters((prev) => ({
      ...prev,
      sortBy: column,
      sortOrder: newOrder,
      page: 1,
    }));
  };

  const getSortIcon = (column) => {
    if (filters.sortBy !== column) return faSort;
    return filters.sortOrder === "ASC" ? faSortUp : faSortDown;
  };

  // Page navigation
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters((prev) => ({ ...prev, page: newPage }));
    }
  };

  // Export report to Excel
  const exportReport = async () => {
    try {
      setExportLoading(true);
      const token = localStorage.getItem("authToken");

      const params = {
        subscriber_code: filters.subscriber_code || undefined,
        fullname: filters.fullname || undefined,
        mobileno: filters.mobileno || undefined,
        email: filters.email || undefined,
        interest: filters.interest || undefined,
        interest_name: filters.interest_name || undefined,
        lead_status: filters.lead_status || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      };

      const response = await axios.get(`${API_URL}report-whatsapp-optin/export`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const today = moment().format("YYYY-MM-DD");
      link.setAttribute("download", `whatsapp-optin-report-${today}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast.success("Report exported successfully.");
    } catch (error) {
      console.error("Error exporting report:", error);
      showToast.error("Failed to export report.");
    } finally {
      setExportLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString || dateString === "0000-00-00 00:00:00") return "----";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "----";
      return moment(date).format("DD-MM-YYYY HH:mm:ss");
    } catch (error) {
      return "----";
    }
  };

  return (
    <div className="panel-content mt-5">
      <style>{`
        #datepicker-portal .react-datepicker-popper,
        .react-datepicker-popper {
          z-index: 9999 !important;
        }
      `}</style>
      {/* ─── Header Controls Bar ────────────────────────────────────────── */}
      <div className="panel-heading d-flex justify-content-between align-items-center" style={{ padding: "8px 5px", borderBottom: "none" }}>
        <div className="col-md-4">
          <div style={{ float: "left", position: "relative" }}>
            {hasAnyFilter() && (
              <button
                className="btn btn-default btn-xs"
                onClick={clearFilters}
                title="Clear all filters"
              >
                Clear Filter
              </button>
            )}
          </div>
        </div>

        <div>
          <span style={{ fontSize: "0.8rem" }} className="me-3">
            {loading ? (
              <span className="skeleton-text" style={{ width: "140px", display: "inline-block" }}></span>
            ) : (
              pagination.showingRecords
            )}
          </span>

          <button
            className="btn btn-default btn-sm mx-1"
            onClick={() => fetchData(filters.page)}
            title="Refresh"
          >
            <FontAwesomeIcon icon={faSync} spin={loading} />
          </button>

          <button
            className="btn btn-default btn-sm mx-1"
            onClick={exportReport}
            disabled={exportLoading || data.length === 0}
            title="Export Report"
          >
            {exportLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Exporting...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faDownload} /> Export Report
              </>
            )}
          </button>

          <button
            className={`btn btn-default btn-sm ${filters.page === 1 ? "d-none" : ""}`}
            title="Previous"
            onClick={() => handlePageChange(filters.page - 1)}
            disabled={filters.page === 1}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          &nbsp;
          <button
            className={`btn btn-default btn-sm ${filters.page === pagination.totalPages ? "d-none" : ""}`}
            title="Next"
            onClick={() => handlePageChange(filters.page + 1)}
            disabled={filters.page === pagination.totalPages}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>

      {/* ─── Main Panel & Report Table ───────────────────────────────────── */}
      <div className="panel panel-default with-rounded-corner with-shadow">
        <div className="table-responsive">
          <table className="table table-condensed table-hover bg-white sticky-table" style={{ width: "100%", margin: 0 }}>
            <thead>
              {/* Header Sort Row */}
              <tr style={{ cursor: "pointer" }}>
                <th style={{ width: "40px", textAlign: "center" }}>#</th>
                <th style={{ width: "120px" }} onClick={() => handleSort("subscriber_code")}>
                  Subscriber ID <FontAwesomeIcon icon={getSortIcon("subscriber_code")} />
                </th>
                <th style={{ minWidth: "150px" }} onClick={() => handleSort("fullname")}>
                  Full Name <FontAwesomeIcon icon={getSortIcon("fullname")} />
                </th>
                <th style={{ width: "125px" }} onClick={() => handleSort("mobileno")}>
                  Mobile Number <FontAwesomeIcon icon={getSortIcon("mobileno")} />
                </th>
                <th style={{ minWidth: "170px" }} onClick={() => handleSort("email")}>
                  Email Address <FontAwesomeIcon icon={getSortIcon("email")} />
                </th>
                <th style={{ width: "110px" }} onClick={() => handleSort("interest")}>
                  Interest <FontAwesomeIcon icon={getSortIcon("interest")} />
                </th>
                <th style={{ minWidth: "150px" }} onClick={() => handleSort("interest_name")}>
                  Stock / Detail <FontAwesomeIcon icon={getSortIcon("interest_name")} />
                </th>
                <th style={{ width: "160px" }} onClick={() => handleSort("created_on")}>
                  Registered On <FontAwesomeIcon icon={getSortIcon("created_on")} />
                </th>
              </tr>

              {/* Filter Row */}
              <tr>
                <th></th>
                <th>
                  <input
                    type="text"
                    name="subscriber_code"
                    placeholder="Search ID..."
                    value={formInputs.subscriber_code}
                    onChange={handleFormInputChange}
                    onKeyDown={handleKeyDown}
                    style={{
                      width: "100px",
                      padding: "5px",
                    }}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    name="fullname"
                    placeholder="Search name..."
                    value={formInputs.fullname}
                    onChange={handleFormInputChange}
                    onKeyDown={handleKeyDown}
                    style={{
                      width: "130px",
                      padding: "5px",
                    }}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    name="mobileno"
                    placeholder="Search mobile..."
                    value={formInputs.mobileno}
                    onChange={handleFormInputChange}
                    onKeyDown={handleKeyDown}
                    style={{
                      width: "110px",
                      padding: "5px",
                    }}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    name="email"
                    placeholder="Search email..."
                    value={formInputs.email}
                    onChange={handleFormInputChange}
                    onKeyDown={handleKeyDown}
                    style={{
                      width: "140px",
                      padding: "5px",
                    }}
                  />
                </th>
                <th>
                  <select
                    name="interest"
                    value={formInputs.interest}
                    onChange={handleInterestSelect}
                    style={{ width: "110px", padding: "5px" }}
                  >
                    <option value="">--select--</option>
                    <option value="stock">Stock</option>
                    <option value="streetview">Streetview</option>
                    {distinctInterests
                      .filter((i) => !["stock", "streetview"].includes(String(i).toLowerCase()))
                      .map((item, idx) => (
                        <option key={idx} value={item}>
                          {item}
                        </option>
                      ))}
                  </select>
                </th>
                <th>
                  <input
                    type="text"
                    name="interest_name"
                    placeholder="Stock / detail..."
                    value={formInputs.interest_name}
                    onChange={handleFormInputChange}
                    onKeyDown={handleKeyDown}
                    style={{
                      width: "130px",
                      padding: "5px",
                    }}
                  />
                </th>
                <th>
                  <div className="d-flex align-items-center gap-1">
                    <DatePicker
                      portalId="datepicker-portal"
                      popperPlacement="bottom-end"
                      popperProps={{ strategy: "fixed" }}
                      selected={formInputs.startDate}
                      onChange={handleDateChange}
                      startDate={formInputs.startDate}
                      endDate={formInputs.endDate}
                      selectsRange
                      isClearable
                      toggleCalendarOnIconClick
                      placeholderText=""
                      dateFormat="dd-MM-yyyy"
                    />
                    <button
                      type="button"
                      className="btn btn-default btn-xs btn-group-xs"
                      onClick={applyFilters}
                      title="Apply Filter"
                    >
                      <FontAwesomeIcon icon={faSearch} />
                    </button>
                  </div>
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                // Skeleton loading rows
                Array.from({ length: 8 }).map((_, skeletonIndex) => (
                  <tr key={`skeleton-${skeletonIndex}`} className="skeleton-row">
                    <td><div className="skeleton-text" style={{ width: "20px" }}></div></td>
                    <td><div className="skeleton-text" style={{ width: "70px" }}></div></td>
                    <td><div className="skeleton-text" style={{ width: "120px" }}></div></td>
                    <td><div className="skeleton-text" style={{ width: "90px" }}></div></td>
                    <td><div className="skeleton-text" style={{ width: "140px" }}></div></td>
                    <td><div className="skeleton-text" style={{ width: "70px" }}></div></td>
                    <td><div className="skeleton-text" style={{ width: "110px" }}></div></td>
                    <td><div className="skeleton-text" style={{ width: "120px" }}></div></td>
                  </tr>
                ))
              ) : data.length > 0 ? (
                data.map((item, idx) => {
                  const subscriberCode = item.subscriber_code || `SUB-${item.subscriberid}`;
                  const subscriberLink = `/Subscriber/edit/${item.subscriberid}`;

                  return (
                    <tr key={item.id || idx}>
                      <td style={{ textAlign: "center" }}>
                        {(pagination.currentPage - 1) * filters.limit + idx + 1}
                      </td>
                      <td>
                        <Link
                          to={subscriberLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "inherit", textDecoration: "none" }}
                          title="Open Subscriber Details"
                        >
                          {subscriberCode}
                        </Link>
                      </td>
                      <td>{item.fullname || "----"}</td>
                      <td>{item.mobileno || "----"}</td>
                      <td>{item.email || "----"}</td>
                      <td>
                        <span style={{ textTransform: "capitalize" }}>
                          {item.interest || "----"}
                        </span>
                      </td>
                      <td>{item.interest_name || "----"}</td>
                      <td>{formatDateTime(item.created_on)}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="text-center py-4" style={{ color: "#999" }}>
                    No WhatsApp opt-in records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WhatsappOptinReport;
